import React, { useEffect, useState } from 'react';
import { Alert, Button, Form, Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { qcResultAPI } from '../../services';
import { RESOLUTION_LABELS } from './QcResultsListTab';

const DEFAULT_RESOLUTION = 'PENALIZED';
// #171552 — QC bắt lỗi ghi mặc định 0,5 điểm; cho phép nhập số thập phân từ 0,5.
const DEFAULT_POINT_VALUE = 0.5;
const MIN_POINT_VALUE = 0.5;

export default function QcHandlingModal({ show, onHide, sourceKind, id, row, onSuccess }) {
  const [resolution, setResolution] = useState(DEFAULT_RESOLUTION);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const [pointType, setPointType] = useState('SUBTRACT');
  const [pointValue, setPointValue] = useState(DEFAULT_POINT_VALUE);
  const [pointReason, setPointReason] = useState('');
  const [pointSaving, setPointSaving] = useState(false);

  useEffect(() => {
    if (!show) return;
    setResolution(row?.handling?.resolution || DEFAULT_RESOLUTION);
    setNote(row?.handling?.note || '');
    setPointType('SUBTRACT');
    setPointValue(DEFAULT_POINT_VALUE);
    setPointReason('');
  }, [show, row]);

  const isHandled = !!row?.handling?.is_handled;
  const hasPoint = !!row?.handling?.point && row.handling.point.status !== 'CANCELLED';

  const handleSaveHandling = async () => {
    try {
      setSaving(true);
      await qcResultAPI.handleQcResult(sourceKind, id, { resolution, note: note || undefined });
      toast.success('Đã lưu xử lý');
      if (onSuccess) onSuccess();
    } catch (e) {
      toast.error(e?.message || 'Không thể lưu xử lý');
    } finally {
      setSaving(false);
    }
  };

  const handleUnhandle = async () => {
    try {
      setSaving(true);
      await qcResultAPI.unhandleQcResult(sourceKind, id);
      toast.success('Đã bỏ đánh dấu xử lý');
      if (onSuccess) onSuccess();
    } catch (e) {
      toast.error(e?.message || 'Không thể bỏ xử lý');
    } finally {
      setSaving(false);
    }
  };

  const handleApplyPoint = async () => {
    if (!pointValue || Number(pointValue) < MIN_POINT_VALUE) {
      toast.error('Giá trị điểm phải >= 0,5');
      return;
    }
    if (!pointReason.trim()) {
      toast.error('Vui lòng nhập lý do');
      return;
    }
    try {
      setPointSaving(true);
      await qcResultAPI.applyQcPoint(sourceKind, id, { type: pointType, value: Number(pointValue), reason: pointReason });
      toast.success('Đã gắn điểm');
      if (onSuccess) onSuccess();
    } catch (e) {
      toast.error(e?.message || 'Không thể gắn điểm');
    } finally {
      setPointSaving(false);
    }
  };

  const handleRevokePoint = async () => {
    if (!window.confirm('Gỡ điểm đã gắn cho kết quả QC này?')) return;
    try {
      setPointSaving(true);
      await qcResultAPI.revokeQcPoint(sourceKind, id);
      toast.success('Đã gỡ điểm');
      if (onSuccess) onSuccess();
    } catch (e) {
      toast.error(e?.message || 'Không thể gỡ điểm');
    } finally {
      setPointSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>Xử lý kết quả QC</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {row?.worker && (
          <div className="mb-3">
            <strong>Nhân viên:</strong> {row.worker.full_name}
          </div>
        )}

        <h6 className="mb-2">Xử lý</h6>
        <Form.Group className="mb-2">
          <Form.Label>Hướng xử lý</Form.Label>
          <Form.Select value={resolution} onChange={(e) => setResolution(e.target.value)}>
            {Object.entries(RESOLUTION_LABELS).map(([k, label]) => (
              <option key={k} value={k}>{label}</option>
            ))}
          </Form.Select>
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Ghi chú</Form.Label>
          <Form.Control as="textarea" rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Ghi chú xử lý (tuỳ chọn)" />
        </Form.Group>
        <div className="d-flex gap-2 mb-4">
          <Button variant="primary" size="sm" onClick={handleSaveHandling} disabled={saving}>
            {isHandled ? 'Cập nhật xử lý' : 'Đánh dấu đã xử lý'}
          </Button>
          {isHandled && (
            <Button variant="outline-secondary" size="sm" onClick={handleUnhandle} disabled={saving}>
              Bỏ xử lý
            </Button>
          )}
        </div>

        <hr />
        <h6 className="mb-2">Điểm thưởng/phạt</h6>
        {hasPoint ? (
          <Alert variant="info" className="d-flex justify-content-between align-items-center">
            <span>
              Đã gắn: <strong>{row.handling.point.type === 'ADD' ? '+' : '-'}{row.handling.point.value} điểm</strong> — {row.handling.point.reason}
            </span>
            <Button variant="outline-danger" size="sm" onClick={handleRevokePoint} disabled={pointSaving}>
              Gỡ điểm
            </Button>
          </Alert>
        ) : (
          <>
            <Form.Group className="mb-2">
              <div>
                <Form.Check
                  inline
                  type="radio"
                  label="Phạt (SUBTRACT)"
                  checked={pointType === 'SUBTRACT'}
                  onChange={() => setPointType('SUBTRACT')}
                />
                <Form.Check
                  inline
                  type="radio"
                  label="Thưởng (ADD)"
                  checked={pointType === 'ADD'}
                  onChange={() => setPointType('ADD')}
                />
              </div>
            </Form.Group>
            {pointType === 'ADD' && (
              <Alert variant="warning" className="py-2 small">
                Lưu ý: gỡ điểm ADD sau này không tự động trừ lại phần đã tích luỹ.
              </Alert>
            )}
            <Form.Group className="mb-2">
              <Form.Label>Giá trị điểm</Form.Label>
              <Form.Control type="number" min={MIN_POINT_VALUE} step={0.5} value={pointValue} onChange={(e) => setPointValue(e.target.value)} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Lý do</Form.Label>
              <Form.Control
                type="text"
                value={pointReason}
                onChange={(e) => setPointReason(e.target.value)}
                placeholder="Nhập lý do cụ thể..."
              />
            </Form.Group>
            <Button variant={pointType === 'ADD' ? 'success' : 'danger'} size="sm" onClick={handleApplyPoint} disabled={pointSaving}>
              Gắn điểm
            </Button>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>Đóng</Button>
      </Modal.Footer>
    </Modal>
  );
}
